// frontend/store/services/productService.js
import { supabase } from '../../supabaseClient';
import Compressor from 'compressorjs';

const compressImageAsync = (file) => {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.9,
            maxWidth: 1920,
            maxHeight: 1920,
            success(result) {
                resolve(new File([result], file.name, { type: file.type, lastModified: Date.now() }));
            },
            error(err) {
                reject(err);
            },
        });
    });
};

/**
 * Handles image compression, uploads to ImageKit, tracks images, and creates the product.
 */
export const createProductWithAssetsWorkflow = async ({ payload, uploadBatch, userId, shopId }) => {
    const publicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
    const uploadedUrls = { center: '', side: '' };
    const trackingRows = [];

    const folderPath = `/porte17/shops/menu/products`;

    // 1. Process files sequentially if real File objects exist
    for (const item of uploadBatch) {
        if (!item.file) continue;

        try {
            const authResponse = await fetch('/.netlify/functions/auth-imagekit?type=product_asset');
            const authData = await authResponse.json();
            if (!authResponse.ok || !authData.signature) throw new Error("ImageKit auth signature unavailable.");

            const compressedFile = await compressImageAsync(item.file);
            const uniqueId = self.crypto.randomUUID().split('-')[0];
            const fileName = `prod-direct-${item.label}-${uniqueId}-${Date.now()}`;

            const formData = new FormData();
            formData.append('file', compressedFile);
            formData.append('fileName', fileName);
            formData.append('publicKey', publicKey);
            formData.append('expire', authData.expire);
            formData.append('token', authData.token);
            formData.append('signature', authData.signature);
            formData.append('folder', folderPath);

            const uploadResponse = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
                method: 'POST',
                body: formData,
            });

            if (!uploadResponse.ok) {
                const errData = await uploadResponse.json();
                throw new Error(errData.message || "Network reject during image upload.");
            }

            const result = await uploadResponse.json();
            uploadedUrls[item.label] = result.url;

            // Prepare dynamic tracked_images database entry
            trackingRows.push({
                file_id: result.fileId,
                url: result.url,
                file_path: result.filePath,
                entity_type: 'product',
                image_type: item.label,
                uploaded_by: userId,
                shop_id: shopId || null, 
                width: result.width || null,
                height: result.height || null,
                file_size_bytes: compressedFile.size
            });

        } catch (err) {
            console.error(`Asset processing sequence blocked at slot: ${item.label}`, err);
            throw new Error(`Asset upload failure on profile slot [${item.label}]: ${err.message}`);
        }
    }

    // 2. Insert tracking records into tracked_images table
    if (trackingRows.length > 0) {
        const { error: trackError } = await supabase.from('tracked_images').insert(trackingRows);
        if (trackError) console.error("Non-blocking tracked mapping error:", trackError);
    }

    // 3. Inject resolution urls directly into the target payload schema
    const finalPayload = {
        ...payload,
        card_images: {
            center: uploadedUrls.center || payload.card_images?.center || '',
            side: uploadedUrls.side || payload.card_images?.side || ''
        }
    };

    const { data, error } = await supabase
        .from('products')
        .insert(finalPayload)
        .select();

    if (error) {
        console.error('Error creating product inside database:', error);
        throw error;
    }

    return data[0];
};


// READ: Fetch all products
export const getAllProducts = async () => {
  const { data, error } = await supabase
    .from('products')
    .select('*');

  if (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
  return data;
};
// READ: Fetch paginated products directly from the database
export const getInfiniteProducts = async ({ pageParam = 1, limit = 20 }) => {
  const startIndex = (pageParam - 1) * limit;
  const endIndex = startIndex + limit - 1; // Supabase range bounds are inclusive

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .range(startIndex, endIndex)
    .order('created_at', { ascending: false }); // Sorts by newest additions

  if (error) {
    console.error(`Error fetching infinite products page ${pageParam}:`, error);
    throw error;
  }

  return {
    items: data || [],
    nextPage: data?.length === limit ? pageParam + 1 : undefined,
  };
};

// READ: Fetch all products for a specific shop
export const getProductsByShop = async (shopId) => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('shop_id', shopId);

  if (error) {
    console.error(`Error fetching products for shop ${shopId}:`, error);
    throw error;
  }
  return data;
};

// READ: Fetch a single product by ID
export const getProductById = async (id) => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('id', id)
    .maybeSingle(); // <-- Use maybeSingle() here

  if (error) {
    console.error(`Error fetching product with ID ${id}:`, error);
    throw error;
  }
  return data;
};

// CREATE: Create a new product
export const createProduct = async (payload) => {
  const { data, error } = await supabase
    .from('products')
    .insert(payload)
    .select();

  if (error) {
    console.error('Error creating product:', error);
    throw error;
  }
  return data[0];
};

// UPDATE: Update an existing product
export const updateProduct = async (id, payload) => {
  const { data, error } = await supabase
    .from('products')
    .update(payload)
    .eq('id', id)
    .select();

  if (error) {
    console.error(`Error updating product with ID ${id}:`, error);
    throw error;
  }
  return data[0];
};

// DELETE: Delete a product by ID
export const deleteProduct = async (id) => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting product with ID ${id}:`, error);
    throw error;
  }
  // The delete method returns no data on success.
  return true;
};

// New: Search products by name or slug
export const searchProducts = async (query) => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .or(`name.ilike.%${query}%,slug.ilike.%${query}%`);

  if (error) {
    console.error('Error searching products:', error);
    throw error;
  }
  return data;
};

// New: Search for a single product by exact slug match
export const getProductBySlug = async (slug) => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
        console.error(`Error fetching product by slug ${slug}:`, error);
        throw error;
    }
    return data;
};

// New: Search for variants by a new product ID
export const getVariantsByProductIdForSearch = async (productId) => {
    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .eq('product_id', productId);
    if (error) throw error;
    return data;
};
///////////////////////////////////////////////////////////

// frontend/store/services/productService.js (searchVariantsByQuery function)

// 🛑 UPDATED: Search for variants using Full-Text Search with Prefix Matching
// src/store/services/productService.js (Assuming 'supabase' is imported/available)

/**
 * Executes a fast, bilingual, partial-word search against the indexed tags 
 * using the Trigram GIN index and ILIKE.
 * @param {string} query The raw user query string.
 * @param {string} lang The user's language (no longer used for FTS config, but kept for future use).
 * @returns {Array} The matching variants.
 */
/**
 * Executes a fast, bilingual, partial-word search against the indexed tags 
 * using the Trigram GIN index and chained ILIKE statements for logical AND matching.
 */
export async function searchVariantsByQuery(query, lang = 'en') {
    // We require at least 2 characters to trigger a search against the DB.
    if (!query || query.trim().length < 2) {
        return [];
    }

    // --- STEP 1: Normalize the Query in JavaScript ---
    const normalizeQuery = (text) => {
        return text.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
            .trim();
    };

    const normalizedQuery = normalizeQuery(query);
    
    // Split the query into individual words
    const words = normalizedQuery.split(/\s+/).filter(Boolean);

    // --- STEP 2: Dynamically Build the Supabase Query ---
    try {
        // Initialize the base query object
        let supabaseQuery = supabase
            .from('variants')
            .select('*')
            .order('created_at', { ascending: false }) 
            .limit(10);

        // 🛑 CRITICAL FIX: Chain an .ilike() filter for EVERY single word.
        // PostgREST interprets multiple parameters on the same column as an "AND" condition.
        words.forEach(word => {
            supabaseQuery = supabaseQuery.ilike('search_tags_vector', `%${word}%`);
        });
            
        // Execute the dynamically built query
        const { data, error } = await supabaseQuery;
            
        if (error) {
            console.error('Supabase ILIKE Search Error:', error);
            throw error;
        }
        
        return data;
        
    } catch (e) {
        console.error('Error in searchVariantsByQuery:', e);
        return [];
    }
}


// NEW: Paginates product data with a limit of 25 items per page
export const getPaginatedProducts = async (page = 0, limit = 25) => {
  const start = page * limit;
  const end = start + limit - 1;
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .range(start, end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching paginated products:', error);
    throw error;
  }
  return data;
};

// NEW: Fetches multiple products by their IDs
export const getProductsByIds = async (ids) => {
  if (!ids || ids.length === 0) {
    return [];
  }
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .in('id', ids);

  if (error) {
    console.error('Error fetching products by IDs:', error);
    throw error;
  }
  return data;
};

// NEW: Updates the recommended list for a product
export const updateProductRecommendedList = async (productId, recommendedIds) => {
  const { data, error } = await supabase
    .from('products')
    .update({ recommended: recommendedIds })
    .eq('id', productId)
    .select();

  if (error) {
    console.error(`Error updating recommended list for product ${productId}:`, error);
    throw error;
  }
  return data[0];
};

// NEW: Updates the accessories list for a product
export const updateProductAccessoriesList = async (productId, accessoriesIds) => {
  const { data, error } = await supabase
    .from('products')
    .update({ accessories: accessoriesIds })
    .eq('id', productId)
    .select();

  if (error) {
    console.error(`Error updating accessories list for product ${productId}:`, error);
    throw error;
  }
  return data[0];
};

// NEW: Searches for a single product by name
export const searchProductByName = async (query) => {
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .ilike('name', `%${query}%`)
        .limit(1);

    if (error) {
        console.error('Error searching product by name:', error);
        throw error;
    }
    return data[0];
};

// NEW: Fetch all products belonging to a specific series
export const getProductsBySeries = async (seriesId) => {
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('series_id', seriesId);

    if (error) {
        console.error(`Error fetching products for series ${seriesId}:`, error);
        throw error;
    }
    return data;
};

// NEW: Searches for products by name and returns multiple results
export const searchProductsByName = async (query) => {
    if (!query) {
        return [];
    }
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .ilike('name', `%${query}%`)
        .limit(10); // Limit results to prevent overwhelming the user

    if (error) {
        console.error('Error searching products by name:', error);
        throw error;
    }
    return data;
};


/**
 * Updates the specs_carousel for a product.
 * @param {string} productId - The ID of the product to update.
 * @param {object} specsCarouselData - The new JSON data for the specs_carousel.
 * @returns {Promise<object>} The updated product data.
 */
export const updateProductSpecsCarousel = async (productId, specsCarouselData) => {
    const { data, error } = await supabase
        .from('products')
        .update({ specs_carousel: specsCarouselData })
        .eq('id', productId)
        .select();

    if (error) {
        console.error(`Error updating specs carousel for product ${productId}:`, error);
        throw error;
    }
    return data[0];
};

/**
 * Fetches products that have an empty specs_carousel object.
 * @param {number} page - The page number to fetch.
 * @param {number} limit - The number of items per page.
 * @returns {Promise<Array>} A list of products with empty specs_carousel.
 */
export const getProductsWithEmptySpecsCarousel = async (page = 0, limit = 10) => {
    const start = page * limit;
    const end = start + limit - 1;
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .contains('specs_carousel', {})
        .order('created_at', { ascending: false })
        .range(start, end);

    if (error) {
        console.error('Error fetching products with empty specs_carousel:', error);
        throw error;
    }
    return data;
};

////////////////////////////////////////////////////////
////////////////////////////////////////////////////////

/**
 * Updates the specsData for a product.
 * @param {string} productId - The ID of the product to update.
 * @param {object} specsData - The new JSON data for the specsData.
 * @returns {Promise<object>} The updated product data.
 */
export const updateProductSpecsData = async (productId, specsData) => {
    const { data, error } = await supabase
        .from('products')
        .update({ specs: specsData })
        .eq('id', productId)
        .select();

    if (error) {
        console.error(`Error updating specs carousel for product ${productId}:`, error);
        throw error;
    }
    return data[0];
};

/**
 * Fetches products that have an empty specsData object.
 * @param {number} page - The page number to fetch.
 * @param {number} limit - The number of items per page.
 * @returns {Promise<Array>} A list of products with empty specsData.
 */
export const getProductsWithEmptySpecsData = async (page = 0, limit = 10) => {
    const start = page * limit;
    const end = start + limit - 1;
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .contains('specs', {})
        .order('created_at', { ascending: false })
        .range(start, end);

    if (error) {
        console.error('Error fetching products with empty specs data:', error);
        throw error;
    }
    return data;
};


/**
 * Fetches a paginated list of products filtered by a specific category name,
 * ordered alphabetically by the product's name.
 * @param {string} categoryName - The category to filter by (e.g., 'Laptops').
 * @param {number} page - The page number (0-indexed).
 * @param {number} limit - The number of items per page.
 * @returns {Promise<Array>} A list of products.
 */
export const getPaginatedProductsByCategory = async (categoryName, page = 0, limit = 20) => {
    if (!categoryName) {
        return [];
    }

    const start = page * limit;
    const end = start + limit - 1;

    const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', categoryName) // Assuming you have a 'category' column
        .order('name', { ascending: true }) // Order alphabetically
        .range(start, end);

    if (error) {
        console.error(`Error fetching paginated products for category ${categoryName}:`, error);
        throw error;
    }
    return data;
};

